import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi } from "../api/adminApi";
import { DataTable } from "../components/DataTable";
import { NavIcon } from "../components/NavIcon";
import { useOnboarding } from "../onboarding/useOnboarding";
import type { ReportDefinition, ReportExecutionLog } from "../api/types";

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function StatCard({
  icon,
  tone,
  label,
  value,
}: {
  icon: string;
  tone: "primary" | "success" | "warning" | "danger";
  label: string;
  value: number;
}) {
  return (
    <div className={`stat-card stat-card-${tone}`}>
      <div className={`stat-card-icon stat-card-icon-${tone}`}>
        <NavIcon name={icon} size={18} />
      </div>
      <div>
        <div className="stat-card-label">{label}</div>
        <div className="stat-card-value">{value}</div>
      </div>
    </div>
  );
}

function OnboardingCard() {
  const { steps, loading, doneCount, requiredDone } = useOnboarding();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem("reportforge.onboarding.dismissed") === "1";
    } catch {
      return false;
    }
  });
  if (loading || dismissed || requiredDone) return null;
  const next = steps.find((st) => !st.done && !st.optional) ?? steps.find((st) => !st.done);
  return (
    <div className="page-panel onboarding-card">
      <div className="onboarding-card-main">
        <div>
          <h3 style={{ margin: 0 }}>Finish setting up — {doneCount} of {steps.length} done</h3>
          <div className="onboarding-bar" style={{ margin: "10px 0 6px" }}>
            <div className="onboarding-bar-fill" style={{ width: `${Math.round((doneCount / steps.length) * 100)}%` }} />
          </div>
          {next && <div className="help-text">Next: <strong>{next.title}</strong> — {next.why}</div>}
        </div>
        <div className="onboarding-card-actions">
          {next && <Link to={next.to} className="btn-primary">{next.cta}</Link>}
          <Link to="/getting-started" className="btn-secondary">See all steps</Link>
          <button
            type="button"
            className="link-button"
            onClick={() => {
              try {
                localStorage.setItem("reportforge.onboarding.dismissed", "1");
              } catch {
                /* ignore */
              }
              setDismissed(true);
            }}
          >
            Hide
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [logs, setLogs] = useState<ReportExecutionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([adminApi.reports.list(), adminApi.executionLogs.list({ size: 20 })])
      .then(([reportList, logList]) => {
        setReports(reportList);
        setLogs(logList);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, []);

  const reportNameById = new Map(reports.map((r) => [r.id, r.displayName]));
  const published = reports.filter((r) => r.status === "PUBLISHED").length;
  const drafts = reports.filter((r) => r.status === "DRAFT").length;
  const failedRecent = logs.filter((l) => l.status === "FAILED").length;

  const recentlyEdited = [...reports]
    .sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""))
    .slice(0, 6);

  return (
    <div>
      <div className="dashboard-hero">
        <div>
          <h1>Welcome back — let's build something.</h1>
          <p>Design a new report, connect its data, and it's ready to run in minutes.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => navigate("/reports")}>
          + New report
        </button>
      </div>

      {error && <div className="error-text">{error}</div>}
      <OnboardingCard />

      {loading && <p>Loading...</p>}

      {!loading && !error && (
        <>
          <div className="stat-grid">
            <StatCard icon="reports" tone="primary" label="Total reports" value={reports.length} />
            <StatCard icon="checkCircle" tone="success" label="Published" value={published} />
            <StatCard icon="edit" tone="warning" label="Drafts" value={drafts} />
            <StatCard icon="alertTriangle" tone="danger" label="Recent failures" value={failedRecent} />
          </div>

          {recentlyEdited.length > 0 && (
            <div className="page-panel">
              <h3>Recently edited reports</h3>
              <div className="report-card-grid">
                {recentlyEdited.map((r) => (
                  <div className={`report-card report-card-${r.status.toLowerCase()}`} key={r.id}>
                    <div className="report-card-top">
                      <div className="report-card-icon">
                        <NavIcon name="reports" size={16} />
                      </div>
                      <span className={`badge badge-${r.status.toLowerCase()}`}>{r.status}</span>
                    </div>
                    <div>
                      <div className="report-card-title">{r.displayName}</div>
                      <div className="report-card-meta">
                        {r.category || "Uncategorized"}
                        {r.updatedAt && <span className="report-card-time"> · edited {relativeTime(r.updatedAt)}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => navigate(`/reports/${r.id}/designer`)}
                    >
                      Open in Designer
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="page-panel">
            <h3>Recent executions</h3>
            {logs.length === 0 ? (
              <div className="empty-state empty-state-icon">
                <NavIcon name="inbox" size={28} />
                <p>No executions yet. Run a report and its history will show up here.</p>
              </div>
            ) : (
              <DataTable
                rows={logs}
                rowKey={(l) => l.id}
                emptyMessage="No executions yet."
                columns={[
                  {
                    key: "reportDefinitionId",
                    header: "Report",
                    render: (l) => reportNameById.get(l.reportDefinitionId) ?? l.reportDefinitionId,
                  },
                  { key: "triggerType", header: "Trigger" },
                  { key: "format", header: "Format" },
                  {
                    key: "status",
                    header: "Status",
                    render: (l) => (
                      <span
                        className={
                          l.status === "SUCCESS"
                            ? "badge badge-published"
                            : l.status === "FAILED"
                            ? "badge"
                            : "badge badge-draft"
                        }
                        style={l.status === "FAILED" ? { background: "#fee2e2", color: "#991b1b" } : undefined}
                      >
                        {l.status}
                      </span>
                    ),
                  },
                  { key: "durationMs", header: "Duration (ms)" },
                  { key: "startedAt", header: "Started" },
                ]}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
